import { useEffect, useState, useMemo, useRef } from "react";
import { axInst } from "../../../Components/Login/Service";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { Loader } from "rsuite";
import WaterMark from "../../../Components/WaterMark/WaterMark";

// --- Column width config ---
const FIRST_COL_WIDTH = 220; // Metric column width
const COL_WIDTH = 85; // Every other column: months, Trend (CY), Totals

// --- Small Sparkline component (no dependency) ---
const Sparkline = ({
  data = [],
  color = "#16a34a", // green-600
  width = 60,
  height = 24,
  strokeWidth = 2,
  showDot = true,
  padding = 3,
}) => {
  // Keep only numeric values
  const vals = data
    .map((v) => (v === null || v === undefined ? null : Number(v)))
    .filter((v) => typeof v === "number" && !Number.isNaN(v));

  if (vals.length === 0) {
    return <svg width={width} height={height} />;
  }

  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const innerW = Math.max(0, width - padding * 2);
  const innerH = Math.max(0, height - padding * 2);

  const xAt = (i) =>
    vals.length === 1
      ? padding + innerW / 2
      : padding + (i * innerW) / (vals.length - 1);

  const yAt = (v) =>
    max === min
      ? padding + innerH / 2
      : padding + innerH - ((v - min) * innerH) / (max - min);

  const points = vals.map((v, i) => `${xAt(i)},${yAt(v)}`).join(" ");

  const lastIndex = vals.length - 1;
  const lastX = xAt(lastIndex);
  const lastY = yAt(vals[lastIndex]);

  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        points={points}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {showDot && <circle cx={lastX} cy={lastY} r="2.5" fill={color} />}
    </svg>
  );
};

// API Calls
const fetchServiceData = async (body) => {
  try {
    const res = await axInst.post(
      "dealer-review/dealer_service_dashboard",
      body,
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error("Service API Error:", error);
    return [];
  }
};

const fetchDivisionDropdown = async () => {
  try {
    const res = await axInst.post("dealer-review/divison_filter");
    return res.data?.distinct_divisions?.buckets ?? [];
  } catch (error) {
    console.error("Dropdown API Error:", error);
    return [];
  }
};

// --- Helpers for formatting & legend mapping ---
const INDIAN_LOCALE = "en-IN";

/** Extract short FY key (e.g., "FY-26") from a legend long label like "FY-2025-26" */
const shortFYFromLegend = (label) => {
  if (!label) return null;
  // try to get the last two digits
  const m = String(label).match(/(\d{2})\s*$/);
  if (m) return `FY-${m[1]}`;
  // fallback: if already short
  const m2 = String(label).match(/FY-(\d{2})$/);
  if (m2) return `FY-${m2[1]}`;
  return null;
};

/** Detect % row by name */
const isPercentRow = (id = "") => /%/.test(String(id));

/** Get currency unit from row label */
const getCurrencyUnit = (id = "") => {
  const s = String(id).toLowerCase();
  if (s.includes("in lakh")) return "L"; // Lakh
  if (s.includes("in crore")) return "Cr"; // Crore
  // fallback: special cases by keyword (optional)
  if (s.includes("revenue")) return "L"; // your data says "Revenue in Lakh"
  if (s.includes("part inventory")) return "L";
  if (s.includes("def sales")) return "Cr";
  return null;
};

/** Format any numeric for display */
const formatNumberDisplay = (raw, { unit = null, isPercent = false } = {}) => {
  if (
    raw === undefined ||
    raw === null ||
    raw === "" ||
    Number.isNaN(Number(raw))
  ) {
    return "-";
  }
  const num = Number(raw);

  if (isPercent) {
    return `${num.toLocaleString(INDIAN_LOCALE, {
      maximumFractionDigits: 2,
    })}%`;
  }

  if (unit === "L") {
    // Value is already in Lakhs in dataset; add ₹ and 'L'
    return `₹${num.toLocaleString(INDIAN_LOCALE, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} L`;
  }

  if (unit === "Cr") {
    // Value is already in Crore in dataset; add ₹ and 'Cr'
    return `₹${num.toLocaleString(INDIAN_LOCALE, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} Cr`;
  }

  // default: comma-separated numeric
  return num.toLocaleString(INDIAN_LOCALE, { maximumFractionDigits: 2 });
};

const ServiceTable = ({ show }) => {
  const [selectedOption, setSelectedOption] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempSelectedOption, setTempSelectedOption] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const dropdownRef = useRef(null);

  // Refs for horizontal scrolling behavior
  const scrollContainerRef = useRef(null);
  const monthHeaderRefs = useRef({});
  const hasAutoScrolledRef = useRef(false);

  const { data = [], submitted } = useSelector((e) => e.filtersDealerReview);

  useEffect(() => {
    const fetchData = async () => {
      const divisions = await fetchDivisionDropdown();
      setDivisionOptions(divisions);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  // Fiscal months Apr -> Mar
  const months = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  const queryBody = useMemo(
    () => ({
      ...data,
      division_name: selectedOption.length ? selectedOption : [],
    }),
    [data, selectedOption],
  );

  const {
    data: serviceData = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["service", submitted, selectedOption],
    queryFn: () => fetchServiceData(queryBody),
    cacheTime: 0,
    staleTime: 0,
  });

  // ---------- NEW: Extract legend and data rows ----------
  const legendBlock = useMemo(
    () => serviceData.find((x) => x && x.financial_year_legend),
    [serviceData],
  );

  const prevYearLegendLong = useMemo(() => {
    if (!legendBlock?.financial_year_legend) return null;
    const obj = legendBlock.financial_year_legend.find(
      (o) => "Previous Year" in o,
    );
    return obj ? obj["Previous Year"] : null;
  }, [legendBlock]);

  const currentYearLegendLong = useMemo(() => {
    if (!legendBlock?.financial_year_legend) return null;
    const obj = legendBlock.financial_year_legend.find(
      (o) => "Current Year" in o,
    );
    return obj ? obj["Current Year"] : null;
  }, [legendBlock]);

  // Short keys mapped from legend (e.g., "FY-26")
  const prevYearFromLegendShort = useMemo(
    () => shortFYFromLegend(prevYearLegendLong),
    [prevYearLegendLong],
  );
  const currentYearFromLegendShort = useMemo(
    () => shortFYFromLegend(currentYearLegendLong),
    [currentYearLegendLong],
  );

  // Filter out the legend item from data rows
  const dataRows = useMemo(
    () => serviceData.filter((x) => !x?.financial_year_legend),
    [serviceData],
  );

  // ------ Dynamic Year Helpers ------
  const getYearNumber = (label) => {
    if (!label) return -Infinity;
    const m = String(label).match(/\d+/g);
    return m ? parseInt(m.join(""), 10) : -Infinity;
  };

  // Dynamically extract all available fiscal years from dataRows and sort
  const years = useMemo(() => {
    const yearSet = new Set();
    dataRows.forEach((item) => {
      months.forEach((month) => {
        if (item[month]) {
          Object.keys(item[month]).forEach((year) => {
            yearSet.add(year);
          });
        }
      });
    });
    return Array.from(yearSet).sort(
      (a, b) => getYearNumber(a) - getYearNumber(b),
    );
  }, [dataRows]);

  // Prefer legend-derived short keys; fallback to sorted years
  const latestYear = useMemo(() => {
    return (
      currentYearFromLegendShort ||
      (years.length ? years[years.length - 1] : null)
    );
  }, [currentYearFromLegendShort, years]);

  const prevYear = useMemo(() => {
    if (prevYearFromLegendShort) return prevYearFromLegendShort;
    if (years.length > 1) return years[years.length - 2];
    return null;
  }, [prevYearFromLegendShort, years]);

  // UI legend display text
  const prevLegendText = prevYearLegendLong || prevYear || "Previous Year";
  const currentLegendText =
    currentYearLegendLong || latestYear || "Current Year";

  const handleCheckboxChange = (e) => {
    const value = e.target.value;
    setTempSelectedOption((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const clearAll = () => {
    setTempSelectedOption([]);
  };

  const handleApplyFilters = () => {
    setSelectedOption(tempSelectedOption);
    setDropdownOpen(false);
  };

  useEffect(() => {
    if (dropdownOpen) {
      setTempSelectedOption(selectedOption);
    }
  }, [dropdownOpen, selectedOption]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDivisionOptions = divisionOptions.filter((option) =>
    option.key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Map JS month (0-11) -> header labels used
  const getCurrentMonthLabel = () => {
    const jsMonth = new Date().getMonth(); // 0=Jan..11=Dec
    const map = {
      0: "Jan",
      1: "Feb",
      2: "Mar",
      3: "Apr",
      4: "May",
      5: "Jun",
      6: "Jul",
      7: "Aug",
      8: "Sep",
      9: "Oct",
      10: "Nov",
      11: "Dec",
    };
    return map[jsMonth];
  };

  // Reset the auto-scroll flag when data/filter changes so we auto-scroll again
  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [submitted, selectedOption, serviceData]);

  // Auto-scroll horizontally to bring current month into view (no vertical scrolling)
  useEffect(() => {
    if (!dataRows?.length || !years?.length || hasAutoScrolledRef.current)
      return;

    const currentMonthLabel = getCurrentMonthLabel();
    if (!months.includes(currentMonthLabel)) return;

    const headerEl = monthHeaderRefs.current[currentMonthLabel];
    const containerEl = scrollContainerRef.current;
    if (!(headerEl && containerEl)) return;

    // Next paint to ensure layout is ready
    requestAnimationFrame(() => {
      const headerRect = headerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      // Compute header position inside the container
      const offsetWithinContainer =
        headerRect.left - containerRect.left + containerEl.scrollLeft;

      // Center the current month group
      const targetLeft =
        offsetWithinContainer -
        (containerEl.clientWidth / 2 - headerEl.clientWidth / 2);

      containerEl.scrollTo({
        left: Math.max(0, targetLeft),
        top: containerEl.scrollTop, // keep vertical position unchanged
        behavior: "smooth",
      });

      hasAutoScrolledRef.current = true;
    });
  }, [dataRows, years, months]);

  if (isFetching)
    return (
      <div className="flex items-center justify-center h-64 w-full">
        <Loader size="lg" />
      </div>
    );
  if (isError) return <div>Error loading data</div>;

  return (
    <div className="bg-[#f9fafb] relative rounded-md">
      <WaterMark />

      {/* Header & Dropdown */}
      <div className="flex justify-between items-center p-2 pb-[1px]">
        <header className="text-lg font-bold text-gray-800">Services</header>
        {!show && (
          <div className="my-1 flex justify-end">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen((prev) => !prev)}
                className="border border-gray-300 text-[12px] bg-white rounded-md w-48 p-0.5 flex items-center justify-between px-2"
              >
                {selectedOption.length > 0
                  ? ` Selected Divisions(${selectedOption.length})`
                  : "Select Divisions"}
                <span
                  className={`ml-2 transform transition-transform text-xl ${
                    dropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <MdOutlineKeyboardArrowDown />
                </span>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                  ref={dropdownRef}
                >
                  <div className="p-2">
                    <input
                      type="text"
                      value={searchTerm}
                      name="search-division"
                      onChange={handleSearchChange}
                      placeholder="Search Division..."
                      className="w-full p-1 border border-gray-300 rounded mb-1"
                    />
                    <div className="flex justify-end px-2 ">
                      <button
                        className="text-blue-600 hover:text-blue-800 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearAll();
                        }}
                      >
                        Clear Selection
                      </button>
                    </div>
                    <div className="max-h-48 overflow-auto">
                      {filteredDivisionOptions.map((option, index) => (
                        <label key={index} className="block text-sm">
                          <input
                            type="checkbox"
                            value={option.key}
                            checked={tempSelectedOption.includes(option.key)}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                          />
                          {option.key}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 text-xs flex items-center gap-2">
                    <button
                      className="px-3 py-1 bg-[#2A5775] w-full text-white "
                      onClick={handleApplyFilters}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="pt-2 border border-gray-300 rounded-md bg-white">
        {/* Dynamic Legend (from API if available) */}
        <div className="flex justify-end gap-4 px-2 mb-1 text-sm font-semibold">
          <div className="flex items-center gap-1 text-sm">
            <div className="w-2 h-2 rounded-full bg-gray-500 border"></div>
            <span className="text-xs text-slate-400">
              {prevLegendText
                ? `${prevLegendText} Previous Year`
                : "Previous Year"}
            </span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <div className="w-2 h-2 rounded-full bg-[#28526E] border"></div>
            <span className="text-xs text-slate-400">
              {currentLegendText
                ? `${currentLegendText} Current Year`
                : "Current Year"}
            </span>
          </div>
        </div>

        {/* Table wrapper with horizontal scroll enabled */}
        <div
          className="drilltable w-full max-w-[1206px] overflow-x-auto mt-1"
          ref={scrollContainerRef}
        >
          {dataRows.length === 0 && (
            <p className="text-center text-gray-600 py-4">Data Not Available</p>
          )}

          <table
            className="w-full min-w-max border-separate border-spacing-0 border border-gray-300 pl-2 pr-8 mb-[13px] table-fixed"
            style={{ tableLayout: "fixed" }}
          >
            {dataRows.length > 0 && (
              <thead>
                <tr>
                  {/* Metric header (first sticky-left column, wider) */}
                  <th
                    className="px-5 sticky left-0 z-30 text-[#334155]  text-left bg-white font-semibold bg-clip-padding whitespace-nowrap"
                    rowSpan={2}
                    style={{
                      minWidth: FIRST_COL_WIDTH,
                      width: FIRST_COL_WIDTH,
                      maxWidth: FIRST_COL_WIDTH,
                    }}
                  >
                    Metric
                  </th>

                  {/* Trend (CY) header (sticky-left, right after Metric) */}
                  <th
                    rowSpan={2}
                    className="p-1 bg-white text-[#6b7280] text-xs md:sticky z-30 border-r-1 border-gray-900 bg-clip-padding whitespace-nowrap"
                    style={{
                      left: FIRST_COL_WIDTH,
                      minWidth: COL_WIDTH,
                      width: COL_WIDTH,
                      maxWidth: COL_WIDTH,
                    }}
                  >
                    Trend (CY)
                  </th>

                  {/* Month headers (each group spans years; group width = years * COL_WIDTH) */}
                  {months.map((month) => (
                    <th
                      key={month}
                      colSpan={years.length}
                      className="p-1 bg-white text-[#334155] text-xs border-r border-r-zinc-400"
                      ref={(el) => {
                        if (el) monthHeaderRefs.current[month] = el;
                      }}
                      style={{
                        minWidth: years.length * COL_WIDTH,
                        width: years.length * COL_WIDTH,
                        maxWidth: years.length * COL_WIDTH,
                      }}
                    >
                      <span className="bg-[#afc6e7] rounded-full px-4 py-[2px]">
                        {month}
                      </span>
                    </th>
                  ))}

                  {/* Totals header block (sticky-right) */}
                  <th
                    colSpan={years.length}
                    className="p-1 bg-white text-[#334155] text-xs md:sticky right-0 z-30 border-r-1 border-gray-900 bg-clip-padding whitespace-nowrap"
                    style={{
                      minWidth: years.length * COL_WIDTH,
                      width: years.length * COL_WIDTH,
                      maxWidth: years.length * COL_WIDTH,
                    }}
                  >
                    <span className="bg-[#afc6e7] rounded-full px-4 py-[2px]">
                      Total
                    </span>
                  </th>
                </tr>

                <tr>
                  {/* Year labels under each month group */}
                  {months.flatMap((month) =>
                    years.map((year, yearIdx) => (
                      <th
                        key={`${month}_${year}`}
                        className={`text-xs bg-white ${
                          yearIdx % 2 === 0 ? "text-gray-500" : "text-[#28526E]"
                        } ${(yearIdx + 1) % 2 === 0 ? "border-r border-r-zinc-400" : ""}`}
                        style={{
                          minWidth: COL_WIDTH,
                          width: COL_WIDTH,
                          maxWidth: COL_WIDTH,
                        }}
                      >
                        {year}
                      </th>
                    )),
                  )}

                  {/* Totals year headers (sticky-right, all same width) */}
                  {years.map((year, index) => (
                    <th
                      key={`total_${year}`}
                      className={`bg-white  ${
                        index % 2 === 0 ? "text-gray-500" : "text-[#28526E]"
                      } text-xs md:sticky right-0 z-10 border-r-1 border-gray-900`}
                      style={{
                        right: (years.length - index - 1) * COL_WIDTH,
                        minWidth: COL_WIDTH,
                        width: COL_WIDTH,
                        maxWidth: COL_WIDTH,
                      }}
                    >
                      {year}
                    </th>
                  ))}
                </tr>
              </thead>
            )}

            {dataRows.length > 0 && (
              <tbody>
                {dataRows.map((item, idx) => {
                  const percentRow = isPercentRow(item.id);
                  const unit = getCurrencyUnit(item.id);
                  // ⭐ NEW: detect NC row
                  const isNCRow = String(item.id).includes("NC");
                  // Compute totals per year
                  const totalsByYear = years.reduce((acc, year) => {
                    let sum = 0;
                    let count = 0;
                    months.forEach((month) => {
                      const v = parseFloat(item[month]?.[year]);
                      if (Number.isFinite(v)) {
                        sum += v;

                        count += 1;
                      }
                    });

                    acc[year] = percentRow
                      ? parseFloat((count ? sum / count : 0).toFixed(2))
                      : parseFloat(sum.toFixed(2));

                    return acc;
                  }, {});

                  // Build CY series

                  const cySeries = months

                    .map((m) => {
                      const v = item[m]?.[latestYear];

                      const num = v == null ? null : parseFloat(v);

                      return Number.isFinite(num) ? num : null;
                    })

                    .filter((v) => v !== null);

                  const cyTotal = latestYear
                    ? totalsByYear[latestYear]
                    : undefined;

                  const pyTotal = prevYear ? totalsByYear[prevYear] : undefined;

                  // ⭐ UPDATED TREND COLOR LOGIC

                  let trendColor = "#64748b";

                  if (latestYear && prevYear) {
                    if (isNCRow) {
                      trendColor =
                        (cyTotal ?? Infinity) < (pyTotal ?? Infinity)
                          ? "#16a34a"
                          : "#dc2626";
                    } else {
                      trendColor =
                        (cyTotal ?? -Infinity) > (pyTotal ?? -Infinity)
                          ? "#16a34a"
                          : "#dc2626";
                    }
                  }

                  return (
                    <tr key={idx} className="hover:bg-gray-100">
                      {/* Metric cell (sticky-left, wider) */}
                      <td
                        className="border border-r-zinc-300 px-3 py-3 text-xs bg-white text-[#6b7280] text-left font-semibold sticky left-0 z-10"
                        style={{
                          minWidth: FIRST_COL_WIDTH,
                          width: FIRST_COL_WIDTH,
                          maxWidth: FIRST_COL_WIDTH,
                        }}
                      >
                        {item.id}
                      </td>

                      {/* Trend (CY) sparkline cell (sticky-left after Metric) */}
                      <td
                        className="border border-r-zinc-300 px-1 py-3 text-xs text-center md:sticky z-10 bg-white"
                        style={{
                          left: FIRST_COL_WIDTH,
                          minWidth: COL_WIDTH,
                          width: COL_WIDTH,
                          maxWidth: COL_WIDTH,
                        }}
                      >
                        <Sparkline
                          data={cySeries}
                          color={trendColor}
                          width={COL_WIDTH - 10}
                        />
                      </td>
                      {/* Month cells */}
                      {months.flatMap((month, monthIdx) =>
                        years.map((year, yearIdx) => {
                          const rawVal = item[month]?.[year];
                          const prevVal =
                            prevYear != null
                              ? item[month]?.[prevYear]
                              : undefined;

                          let color = "#64748b";

                          if (latestYear && year === latestYear && prevYear) {
                            if (isNCRow) {
                              color =
                                (rawVal ?? Infinity) < (prevVal ?? Infinity)
                                  ? "green"
                                  : "red";
                            } else {
                              color =
                                (rawVal ?? -Infinity) > (prevVal ?? -Infinity)
                                  ? "green"
                                  : "red";
                            }
                          }

                          return (
                            <td
                              key={`${month}_${year}_${idx}`}
                              className={`border px-1 py-3 text-xs text-center whitespace-nowrap ${
                                (monthIdx * years.length + yearIdx + 1) % 2 ===
                                0
                                  ? "border-r-1 border-r-zinc-400"
                                  : ""
                              }`}
                              style={{
                                color,
                                minWidth: COL_WIDTH,
                                width: COL_WIDTH,
                                maxWidth: COL_WIDTH,
                              }}
                            >
                              {rawVal !== undefined
                                ? formatNumberDisplay(rawVal, {
                                    unit,
                                    isPercent: percentRow,
                                  })
                                : "-"}
                            </td>
                          );
                        }),
                      )}
                      {/* TOTALS */}
                      {years.map((year, index) => {
                        const val = totalsByYear[year];
                        const prevTotal = prevYear
                          ? totalsByYear[prevYear]
                          : undefined;

                        let color = "#64748b";

                        if (latestYear && year === latestYear && prevYear) {
                          if (isNCRow) {
                            color =
                              (val ?? Infinity) < (prevTotal ?? Infinity)
                                ? "green"
                                : "red";
                          } else {
                            color =
                              (val ?? -Infinity) > (prevTotal ?? -Infinity)
                                ? "green"
                                : "red";
                          }
                        }

                        return (
                          <td
                            key={`total_${year}_${idx}`}
                            className={`border px-1 py-3 text-xs text-center md:sticky right-0 z-10 bg-slate-100  ${
                              (index + 1) % 2 === 0
                                ? "border-r-1 border-r-zinc-400"
                                : ""
                            }`}
                            style={{
                              right: (years.length - index - 1) * COL_WIDTH,
                              color,
                              minWidth: COL_WIDTH,
                              width: COL_WIDTH,
                              maxWidth: COL_WIDTH,
                            }}
                          >
                            {val !== undefined
                              ? formatNumberDisplay(val, {
                                  unit,
                                  isPercent: percentRow,
                                })
                              : "-"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceTable;
